/**
 * Servicio académico
 * Maneja toda la lógica del sistema académico: objetivos medibles, rúbricas, progreso
 * Funciona con datos locales y en memoria
 */

import { authService } from './authService';
import configService from './configService';
import curriculumData from '../../curriculum_data.json';
import { RUBRIC_WEIGHTS } from '../config/rubricConfig';
import { personalizedTaskService } from './personalizedTaskService';

export interface LessonObjective {
  id: string;
  level: string;
  unit: number;
  lesson: number;
  description: string;
  order_index: number;
  created_at?: string;
}

export interface LessonRubric {
  level: string;
  unit: number;
  lesson: number;
  rubric_weights: {
    'Fluidez comunicativa': number;
    'Vocabulario': number;
    'Gramática': number;
    'Pronunciación': number;
  };
}

export interface ObjectiveStatus {
  completed: boolean;
  correct_count: number; // Contador de veces que se ha cumplido el objetivo
  total_correct: number; // Para estadísticas
  completed_at?: string;
  // Para objetivos de vocabulario: rastrear palabras diferentes usadas
  vocabulary_words_used?: string[]; // Array de palabras diferentes ya usadas
  // Rastrear frases ya usadas para evitar repetición
  used_phrases?: string[]; // Array de frases (normalizadas) ya usadas para este objetivo
}

export interface UserLessonProgress {
  id?: string;
  user_email: string;
  level: string;
  unit: number;
  lesson: number;
  objectives_status: Record<string, ObjectiveStatus>;
  personalized_task?: string; // Tarea personalizada generada para esta lección
  task_version?: number; // Número de versión de la tarea (incrementa cada vez que se regenera)
  rubric_evaluation?: any;
  all_objectives_completed: boolean;
  completion_date?: string;
  final_score?: number;
  attempts: number;
  approved_on_attempt?: number; // Número de intento en el que se aprobó (score >= 70)
  last_attempt_date?: string;
  created_at?: string;
  updated_at?: string;
}

export interface AcademicLevel {
  level: string;
  unit?: number;
  lesson?: number;
}

export interface LevelProgress {
  unit: number;
  lesson: number;
  completed: boolean;
  inProgress: boolean;
  unlocked: boolean; // Si la lección está desbloqueada (la anterior está completada)
  completionDate?: string;
  hasEvaluation?: boolean; // Si la lección tiene evaluación completada
}

// Mapeo de niveles base a subniveles disponibles
const BASE_LEVEL_TO_SUBLEVELS: Record<string, string[]> = {
  'A1': ['A1.1', 'A1.2'],
  'A2': ['A2.1', 'A2.2', 'A2.3'],
  'B1': ['B1.1', 'B1.2', 'B1.3', 'B1.4', 'B1.5'],
  'B2': ['B2.1', 'B2.2', 'B2.3', 'B2.4', 'B2.5', 'B2.6'],
  'C1': ['C1.A', 'C1.B', 'C1.C', 'C1.D', 'C1.E'],
  'C2': ['C2.A', 'C2.B', 'C2.C', 'C2.D', 'C2.E'],
};

// Almacenamiento en memoria para progreso de lecciones (sin persistencia)
const inMemoryProgress: Map<string, UserLessonProgress> = new Map();

// Generar clave única para progreso
const getProgressKey = (email: string, level: string, unit: number, lesson: number): string => {
  return `${email}-${level}-${unit}-${lesson}`;
};

class AcademicService {
  /**
   * Determina el subnivel activo basado en el progreso del usuario
   * Si encuentra un subnivel completado, retorna el siguiente
   * Si no hay progreso previo, retorna el subnivel N.1 (A1.1, A2.1, etc.)
   */
  private async determineActiveSublevel(email: string, baseLevel: string): Promise<string | null> {
    try {
      // Obtener subniveles disponibles para este nivel base
      const sublevels = BASE_LEVEL_TO_SUBLEVELS[baseLevel];
      if (!sublevels || sublevels.length === 0) {
        return null;
      }

      // Obtener el email del usuario desde configService si no se proporciona
      const userEmail = email || configService.getConfig().email;
      
      if (!userEmail) {
        // console.warn('[AcademicService] ⚠️ No se puede determinar subnivel: no hay email disponible');
        return sublevels[0]; // Retornar el primero por defecto
      }

      // Verificar progreso de cada subnivel en orden
      let lastCompletedIndex = -1;
      
      for (let i = 0; i < sublevels.length; i++) {
        const sublevel = sublevels[i];
        const isCompleted = await this.checkLevelCompletion(userEmail, sublevel);
        
        if (isCompleted) {
          lastCompletedIndex = i;
        } else {
          // Si encontramos uno no completado, no necesitamos verificar los siguientes
          break;
        }
      }

      // Determinar el subnivel activo
      if (lastCompletedIndex === -1) {
        // Ningún subnivel completado, retornar el primero (N.1)
        return sublevels[0];
      } else if (lastCompletedIndex < sublevels.length - 1) {
        // Hay un subnivel completado y hay siguiente disponible
        const nextSublevel = sublevels[lastCompletedIndex + 1];
        return nextSublevel;
      } else {
        // Todos los subniveles están completados, retornar el último
        return sublevels[sublevels.length - 1];
      }
    } catch (error) {
      // En caso de error, retornar el primero por defecto
      const sublevels = BASE_LEVEL_TO_SUBLEVELS[baseLevel];
      return sublevels && sublevels.length > 0 ? sublevels[0] : null;
    }
  }

  /**
   * Obtiene el nivel académico del usuario
   * Retorna nivel por defecto A1.1
   */
  async getUserAcademicLevel(): Promise<AcademicLevel | null> {
    try {
      return { level: 'A1.1' };
    } catch (error) {
      // console.error('[AcademicService] ❌ Error al obtener nivel académico:', error);
      return null;
    }
  }

  /**
   * Carga objetivos medibles de una lección desde curriculum_data.json
   */
  async getLessonObjectives(
    level: string,
    unit: number,
    lesson: number
  ): Promise<LessonObjective[]> {
    try {
      // Buscar la lección en curriculum_data.json
      const lessonData = (curriculumData.lessons as any[]).find(
        l => l.level === level && l.unit === unit && l.lesson === lesson
      );

      if (!lessonData || !lessonData.objectives) {
        return [];
      }

      // Convertir objetivos al formato esperado
      return lessonData.objectives.map((obj: any, index: number) => ({
        id: `${level}-${unit}-${lesson}-${index}`,
        level,
        unit,
        lesson,
        description: obj.description,
        order_index: index,
      }));
    } catch (error) {
      // console.error('[AcademicService] Error al cargar objetivos:', error);
      return [];
    }
  }

  /**
   * Carga rúbrica (pesos) de una lección
   * Retorna pesos fijos desde la configuración
   */
  async getLessonRubric(
    level: string,
    unit: number,
    lesson: number
  ): Promise<LessonRubric | null> {
    try {
      return {
        level,
        unit,
        lesson,
        rubric_weights: RUBRIC_WEIGHTS,
      };
    } catch (error) {
      // console.error('[AcademicService] Error al cargar rúbrica:', error);
      return null;
    }
  }

  /**
   * Obtiene progreso del usuario en una lección (en memoria)
   */
  async getUserLessonProgress(
    email: string,
    level: string,
    unit: number,
    lesson: number
  ): Promise<UserLessonProgress | null> {
    try {
      const key = getProgressKey(email, level, unit, lesson);
      
      // Verificar si ya existe en memoria
      if (inMemoryProgress.has(key)) {
        return inMemoryProgress.get(key)!;
      }

      // Crear nuevo progreso
      const objectives = await this.getLessonObjectives(level, unit, lesson);
      const initialStatus: Record<string, ObjectiveStatus> = {};
      
      objectives.forEach(obj => {
        initialStatus[obj.description] = {
          completed: false,
          correct_count: 0,
          total_correct: 0,
          used_phrases: [],
        };
      });

      const newProgress: UserLessonProgress = {
        user_email: email,
        level,
        unit,
        lesson,
        objectives_status: initialStatus,
        all_objectives_completed: false,
        attempts: 0,
        created_at: new Date().toISOString(),
      };

      // Guardar en memoria
      inMemoryProgress.set(key, newProgress);

      return newProgress;
    } catch (error) {
      // console.error('[AcademicService] Error al obtener progreso:', error);
      return null;
    }
  }

  /**
   * Actualiza el estado de un objetivo medible (en memoria)
   */
  async updateObjectiveStatus(
    email: string,
    level: string,
    unit: number,
    lesson: number,
    objectiveDescription: string,
    isCorrect: boolean,
    vocabularyWordsFound?: string[],
    userMessage?: string
  ): Promise<UserLessonProgress | null> {
    try {
      const progress = await this.getUserLessonProgress(email, level, unit, lesson);
      if (!progress) {
        return null;
      }

      let objectiveStatus = progress.objectives_status?.[objectiveDescription];
      if (!objectiveStatus || typeof objectiveStatus !== 'object') {
        // console.error('[AcademicService] Objetivo no encontrado o inválido:', objectiveDescription);
        return null;
      }

      // Inicializar arrays si no existen
      if (!objectiveStatus.vocabulary_words_used) {
        objectiveStatus.vocabulary_words_used = [];
      }
      if (!objectiveStatus.used_phrases) {
        objectiveStatus.used_phrases = [];
      }
      
      // Agregar frase a la lista de usadas si es correcta
      if (userMessage && isCorrect) {
        const normalizedMessage = userMessage.toLowerCase().trim();
        
        Object.values(progress.objectives_status).forEach(status => {
          if (!status.used_phrases) {
            status.used_phrases = [];
          }
          if (!status.used_phrases.includes(normalizedMessage)) {
            status.used_phrases.push(normalizedMessage);
          }
        });
      }
      
      // Para objetivos de vocabulario
      if (vocabularyWordsFound && vocabularyWordsFound.length > 0) {
        const newWords = vocabularyWordsFound.filter(word => 
          !objectiveStatus.vocabulary_words_used!.includes(word.toLowerCase())
        );
        
        if (newWords.length > 0) {
          objectiveStatus.vocabulary_words_used.push(...newWords.map(w => w.toLowerCase()));
          objectiveStatus.correct_count = objectiveStatus.vocabulary_words_used.length;
          objectiveStatus.total_correct += newWords.length;
          
          if (objectiveStatus.correct_count >= 1) {
            objectiveStatus.completed = true;
            objectiveStatus.completed_at = new Date().toISOString();
          }
        } else {
          isCorrect = false;
        }
      }
      
      // Lógica estándar para objetivos no-vocabulario
      if (isCorrect && (!vocabularyWordsFound || vocabularyWordsFound.length === 0)) {
        objectiveStatus.correct_count += 1;
        objectiveStatus.total_correct += 1;
        
        if (objectiveStatus.correct_count >= 1) {
          objectiveStatus.completed = true;
          objectiveStatus.completed_at = new Date().toISOString();
        }
      }

      // Verificar si todos los objetivos están completados
      const allCompleted = Object.values(progress.objectives_status).every(
        obj => obj.completed
      );

      progress.all_objectives_completed = allCompleted;
      progress.last_attempt_date = new Date().toISOString();
      progress.attempts += 1;
      progress.updated_at = new Date().toISOString();

      // Actualizar en memoria
      const key = getProgressKey(email, level, unit, lesson);
      inMemoryProgress.set(key, progress);

      return progress;
    } catch (error) {
      // console.error('[AcademicService] Error al actualizar progreso:', error);
      return null;
    }
  }

  /**
   * Resetea el progreso de una lección (en memoria)
   */
  async resetLessonProgress(
    email: string,
    level: string,
    unit: number,
    lesson: number
  ): Promise<UserLessonProgress | null> {
    try {
      const objectives = await this.getLessonObjectives(level, unit, lesson);
      const initialStatus: Record<string, ObjectiveStatus> = {};
      
      objectives.forEach(obj => {
        initialStatus[obj.description] = {
          completed: false,
          correct_count: 0,
          total_correct: 0,
          vocabulary_words_used: [],
          used_phrases: [],
        };
      });

      const key = getProgressKey(email, level, unit, lesson);
      const existingProgress = inMemoryProgress.get(key);
      
      const resetProgress: UserLessonProgress = {
        user_email: email,
        level,
        unit,
        lesson,
        objectives_status: initialStatus,
        all_objectives_completed: false,
        rubric_evaluation: null,
        final_score: undefined,
        completion_date: undefined,
        attempts: existingProgress?.attempts || 0,
        last_attempt_date: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      inMemoryProgress.set(key, resetProgress);

      return resetProgress;
    } catch (error) {
      // console.error('[AcademicService] Error al resetear progreso:', error);
      return null;
    }
  }

  /**
   * Verifica si todos los objetivos están completados
   */
  async checkAllObjectivesCompleted(
    email: string,
    level: string,
    unit: number,
    lesson: number
  ): Promise<boolean> {
    try {
      const progress = await this.getUserLessonProgress(email, level, unit, lesson);
      if (!progress) {
        return false;
      }

      return progress.all_objectives_completed;
    } catch (error) {
      return false;
    }
  }

  /**
   * Genera actividad conjunta para una lección usando LLM
   */
  async generateLessonActivity(
    level: string,
    unit: number,
    lesson: number,
    _userContext?: any
  ): Promise<string> {
    const lessonData = (curriculumData.lessons as any[]).find(
      l => l.level === level && l.unit === unit && l.lesson === lesson
    );

    if (!lessonData) {
      return 'Actividad no encontrada';
    }

    return `Actividad para ${lessonData.description}. Objetivos: ${lessonData.objectives.map((o: any) => o.description).join(', ')}`;
  }

  /**
   * Verifica si completó la lección y desbloquea siguiente
   */
  async checkLessonCompletion(
    email: string,
    level: string,
    unit: number,
    lesson: number
  ): Promise<boolean> {
    try {
      const progress = await this.getUserLessonProgress(email, level, unit, lesson);
      if (!progress || !progress.all_objectives_completed) {
        return false;
      }

      return true;
    } catch (error) {
      // console.error('[AcademicService] Error al verificar completitud de lección:', error);
      return false;
    }
  }

  /**
   * Verifica si completó el nivel completo
   */
  async checkLevelCompletion(email: string, level: string): Promise<boolean> {
    try {
      // Obtener todas las lecciones del nivel desde curriculum_data.json
      const lessons = (curriculumData.lessons as any[]).filter(l => l.level === level);

      if (!lessons || lessons.length === 0) {
        return false;
      }

      // Obtener lecciones únicas
      const uniqueLessons = new Set(lessons.map(l => `${l.level}-${l.unit}-${l.lesson}`));
      
      // Verificar que todas las lecciones estén completadas
      for (const lessonKey of uniqueLessons) {
        const [l, u, les] = lessonKey.split('-');
        const progress = await this.getUserLessonProgress(email, l, parseInt(u), parseInt(les));
        
        if (!progress || !progress.all_objectives_completed) {
          return false;
        }
      }

      return true;
    } catch (error) {
      // console.error('[AcademicService] Error al verificar completitud de nivel:', error);
      return false;
    }
  }

  /**
   * Obtiene el progreso de todas las unidades y lecciones de un nivel
   */
  async getLevelProgress(email: string, level: string): Promise<LevelProgress[]> {
    try {
      // Obtener todas las lecciones del nivel desde curriculum_data.json
      const lessons = (curriculumData.lessons as any[]).filter(l => l.level === level);

      if (!lessons || lessons.length === 0) {
        return [];
      }

      // Obtener lecciones únicas
      const uniqueLessons = new Map<string, { unit: number; lesson: number }>();
      lessons.forEach(l => {
        const key = `${l.unit}-${l.lesson}`;
        if (!uniqueLessons.has(key)) {
          uniqueLessons.set(key, { unit: l.unit, lesson: l.lesson });
        }
      });

      // Obtener progreso de cada lección
      const progressPromises = Array.from(uniqueLessons.values()).map(async ({ unit, lesson }) => {
        const progress = await this.getUserLessonProgress(email, level, unit, lesson);
        
        let inProgress = false;
        if (progress && !progress.all_objectives_completed) {
          const hasRealProgress = Object.values(progress.objectives_status).some((status: any) => 
            (status.correct_count > 0) ||
            (status.total_correct > 0)
          );
          inProgress = hasRealProgress;
        }
        
        let hasEvaluation = false;
        if (progress?.rubric_evaluation) {
          const evaluation = progress.rubric_evaluation;
          if (typeof evaluation === 'object' && evaluation !== null) {
            hasEvaluation = Object.keys(evaluation).length > 0;
          } else if (evaluation !== null && evaluation !== undefined && evaluation !== '') {
            hasEvaluation = true;
          }
        }
        
        if (!hasEvaluation && progress?.final_score !== null && progress?.final_score !== undefined) {
          hasEvaluation = true;
        }
        
        if (!hasEvaluation && progress?.all_objectives_completed && progress?.completion_date) {
          hasEvaluation = true;
        }
        
        return {
          unit,
          lesson,
          completed: progress?.all_objectives_completed || false,
          inProgress,
          completionDate: progress?.completion_date || undefined,
          unlocked: false,
          hasEvaluation,
        };
      });

      const progressResults = await Promise.all(progressPromises);
      
      // Ordenar por unidad y lección
      progressResults.sort((a, b) => {
        if (a.unit !== b.unit) return a.unit - b.unit;
        return a.lesson - b.lesson;
      });
      
      // La primera lección siempre está desbloqueada
      if (progressResults.length > 0) {
        progressResults[0].unlocked = true;
      }
      
      // Calcular qué lecciones están desbloqueadas
      for (let i = 1; i < progressResults.length; i++) {
        const current = progressResults[i];
        const previous = progressResults[i - 1];
        
        if (current.inProgress || current.completed) {
          current.unlocked = true;
          continue;
        }
        
        if (current.unit === previous.unit) {
          current.unlocked = previous.completed;
        } else {
          const previousUnitLessons = progressResults.filter(p => p.unit === previous.unit);
          const previousUnitLastLesson = previousUnitLessons.length > 0
            ? previousUnitLessons.sort((a, b) => b.lesson - a.lesson)[0]
            : null;
          
          current.unlocked = previousUnitLastLesson?.completed || false;
        }
      }
      
      return progressResults;
    } catch (error) {
      return [];
    }
  }

  /**
   * Encuentra la lección más nueva desbloqueada o en progreso
   */
  findLatestUnlockedLesson(levelProgress: LevelProgress[]): { unit: number; lesson: number } | null {
    if (levelProgress.length === 0) {
      return null;
    }

    const sorted = [...levelProgress].sort((a, b) => {
      if (a.unit !== b.unit) return b.unit - a.unit;
      return b.lesson - a.lesson;
    });

    const latest = sorted.find(p => p.unlocked || p.inProgress || p.completed);
    
    if (latest) {
      return { unit: latest.unit, lesson: latest.lesson };
    }

    const first = sorted[sorted.length - 1];
    return { unit: first.unit, lesson: first.lesson };
  }

  /**
   * Obtiene o genera una tarea personalizada para un estudiante y lección
   */
  async getPersonalizedTask(
    email: string,
    level: string,
    unit: number,
    lesson: number,
    userAge?: number,
    userCity?: string,
    additionalVariables?: Record<string, any>
  ): Promise<string | null> {
    try {
      return await personalizedTaskService.getOrGenerateTask(
        email,
        level,
        unit,
        lesson,
        userAge,
        userCity,
        additionalVariables
      );
    } catch (error) {
      // console.error('[AcademicService] Error al obtener tarea personalizada:', error);
      return null;
    }
  }

  /**
   * Actualiza campos específicos de user_lesson_progress (en memoria)
   */
  async updateUserLessonProgress(
    email: string,
    level: string,
    unit: number,
    lesson: number,
    updates: Partial<UserLessonProgress>
  ): Promise<UserLessonProgress | null> {
    try {
      const key = getProgressKey(email, level, unit, lesson);
      const progress = inMemoryProgress.get(key);
      
      if (!progress) {
        return null;
      }

      const updatedProgress = {
        ...progress,
        ...updates,
        updated_at: new Date().toISOString(),
      };

      inMemoryProgress.set(key, updatedProgress);

      return updatedProgress;
    } catch (error) {
      // console.error('[AcademicService] Error al actualizar progreso:', error);
      return null;
    }
  }

  /**
   * Crea un nuevo registro de user_lesson_progress (en memoria)
   */
  async createUserLessonProgress(
    email: string,
    level: string,
    unit: number,
    lesson: number,
    initialData: Partial<UserLessonProgress>
  ): Promise<UserLessonProgress | null> {
    try {
      const key = getProgressKey(email, level, unit, lesson);
      
      const newProgress: UserLessonProgress = {
        user_email: email,
        level,
        unit,
        lesson,
        objectives_status: initialData.objectives_status || {},
        personalized_task: initialData.personalized_task,
        all_objectives_completed: false,
        attempts: 0,
        ...initialData,
        created_at: new Date().toISOString(),
      };

      inMemoryProgress.set(key, newProgress);

      return newProgress;
    } catch (error) {
      return null;
    }
  }
}

export const academicService = new AcademicService();
export default academicService;
